// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMantineTheme } from '@mantine/core';
import { Alert, Anchor, Badge, Box, Button, Center, Container, Stack, Text, TextInput, Title } from '@/components/ui';
import { IconMail, IconArrowLeft, IconKey, IconCheck, IconBolt } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { NeonCard } from '../components';

export default function ForgotDarkMode() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const theme = useMantineTheme();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const inputStyles = {
    input: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      borderColor: 'rgba(255, 102, 0, 0.3)',
      color: 'white',
      '&:focus': {
        borderColor: '#ff6600',
        boxShadow: '0 0 10px rgba(255, 102, 0, 0.3)',
      },
    },
    label: { color: '#ff6600' },
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0f 0%, #1a100a 50%, #0f0a0a 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Grid background */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255, 102, 0, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 102, 0, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          pointerEvents: 'none',
        }}
      />

      {/* Glow effects */}
      <Box
        style={{
          position: 'absolute',
          top: '20%',
          left: '15%',
          width: 300,
          height: 300,
          background: 'radial-gradient(circle, rgba(255, 102, 0, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: 250,
          height: 250,
          background: 'radial-gradient(circle, rgba(255, 153, 51, 0.1) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <SlideIn direction="up" delay={100}>
            <NeonCard color="orange" animated glass>
              {submitted ? (
                <Stack gap="lg" align="center">
                  <FadeIn delay={100}>
                    <Center
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 20,
                        backgroundColor: 'rgba(0, 255, 102, 0.2)',
                        boxShadow: '0 0 30px rgba(0, 255, 102, 0.3)',
                      }}
                    >
                      <IconCheck size={40} color="#00ff66" />
                    </Center>
                  </FadeIn>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Title order={2} ta="center" c="white">TRANSMISSION SENT</Title>
                      <Text c="dimmed" ta="center" maw={300}>
                        Reset protocol dispatched to <Text span c="orange" fw={600}>{email}</Text>
                      </Text>
                    </Stack>
                  </FadeIn>

                  <FadeIn delay={300}>
                    <Alert
                      color="orange"
                      variant="outline"
                      w="100%"
                      styles={{
                        root: { backgroundColor: 'rgba(255, 102, 0, 0.1)', borderColor: 'rgba(255, 102, 0, 0.3)' },
                        message: { color: 'white' },
                      }}
                    >
                      <Text size="sm">
                        No signal received? Check spam filters or{' '}
                        <Anchor c="orange" onClick={() => setSubmitted(false)}>retry transmission</Anchor>
                      </Text>
                    </Alert>
                  </FadeIn>

                  <FadeIn delay={400}>
                    <Button
                      component={Link}
                      to="/demo/auth-pages/login/neon"
                      variant="subtle"
                      c="orange"
                      leftSection={<IconArrowLeft size={16} />}
                    >
                      Return to terminal
                    </Button>
                  </FadeIn>
                </Stack>
              ) : (
                <Stack gap="lg">
                  <Button
                    component={Link}
                    to="/demo/auth-pages/forgot-password"
                    variant="subtle"
                    size="xs"
                    leftSection={<IconArrowLeft size={14} />}
                    w="fit-content"
                    c="orange"
                  >
                    Back to variants
                  </Button>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Badge
                        leftSection={<IconBolt size={12} />}
                        variant="filled"
                        color="orange"
                        size="lg"
                        style={{
                          boxShadow: '0 0 20px rgba(255, 102, 0, 0.5)',
                        }}
                      >
                        Recovery Protocol
                      </Badge>
                      <Center
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 14,
                          backgroundColor: 'rgba(255, 102, 0, 0.2)',
                          marginTop: 'var(--mantine-spacing-sm)',
                        }}
                      >
                        <IconKey size={28} color="#ff6600" />
                      </Center>
                      <Title order={2} ta="center" mt="sm" c="white">
                        ACCESS RECOVERY
                      </Title>
                      <Text size="sm" ta="center" c="dimmed" maw={300}>
                        Initialize password reset sequence
                      </Text>
                    </Stack>
                  </FadeIn>

                  <form onSubmit={handleSubmit}>
                    <Stack gap="md">
                      <FadeIn delay={300}>
                        <TextInput
                          label="Email"
                          placeholder="user@system.net"
                          leftSection={<IconMail size={16} />}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          size="md"
                          radius="md"
                          styles={inputStyles}
                        />
                      </FadeIn>

                      <FadeIn delay={400}>
                        <Button
                          type="submit"
                          fullWidth
                          size="md"
                          radius="md"
                          color="orange"
                          variant="filled"
                          style={{
                            boxShadow: '0 0 20px rgba(255, 102, 0, 0.4)',
                          }}
                        >
                          TRANSMIT RESET CODE
                        </Button>
                      </FadeIn>
                    </Stack>
                  </form>

                  <FadeIn delay={500}>
                    <Center>
                      <Anchor component={Link} to="/demo/auth-pages/login/neon" size="sm" c="orange">
                        <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                        Return to login terminal
                      </Anchor>
                    </Center>
                  </FadeIn>
                </Stack>
              )}
            </NeonCard>
          </SlideIn>
        </Container>
      </Center>
    </Box>
  );
}
